declare global {
  interface Window {
    dataLayer: Record<string, unknown>[]
    /**
     * Signature (`<user_id>|<phone hash>`) of the identity already on the
     * dataLayer. Shared between `gtmIdentityScript`, which runs in <head>
     * before GTM, and `pushUserIdentity` below, which covers the loads that
     * script cannot see — so the same visitor is never pushed twice.
     */
    __gtmIdentity?: string
  }
}

/**
 * Pushes a marketing-defined event onto the GTM dataLayer (GTM-KB57HH85,
 * bootstrapped in the root layout). Distinct from `trackEvent` in
 * `./analytics.ts`, which is this app's own internal event bus — this one is
 * consumed directly by Tag Manager triggers (GA4, ads conversions, etc.).
 *
 * No-ops outside the browser and swallows failures, matching `trackEvent`'s
 * contract: analytics must never be able to break the page.
 */
export function pushDataLayerEvent(payload: Record<string, unknown>) {
  if (typeof window === 'undefined') return
  try {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(payload)
  } catch {
    /* ignore */
  }
}

/**
 * Pushes a conversion event and waits until GTM has finished processing it.
 *
 * Navigation immediately after a successful write can otherwise outrun tags
 * that still need to build and dispatch their network request. GTM invokes
 * `eventCallback` after every matching tag settles; `eventTimeout` and the
 * local timer keep analytics from ever blocking the product flow.
 */
export function pushDataLayerEventAndWait(
  payload: Record<string, unknown>,
  timeoutMs = 800,
): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()

  return new Promise((resolve) => {
    let settled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    const finish = () => {
      if (settled) return
      settled = true
      if (timer) clearTimeout(timer)
      resolve()
    }

    timer = setTimeout(finish, timeoutMs)
    pushDataLayerEvent({
      ...payload,
      eventCallback: finish,
      eventTimeout: timeoutMs,
    })
  })
}

// ─── User identity (user_id / hashed phone) ──────────────────────────────────

/**
 * Where the SHA-256 of the session user's mobile is cached.
 *
 * Hashing is asynchronous (Web Crypto), and the identity has to be on the
 * dataLayer *before* GTM initialises. Nothing can await inside a blocking
 * <head> script, so the hash is computed once after login and read back
 * synchronously on every later page load.
 */
const PHONE_HASH_KEY = 'gtmPhoneHash'

interface CachedPhoneHash {
  /** The account the hash belongs to — a mobile number cannot change hands. */
  userId: string
  /** E.164 form that was hashed, so a re-normalisation change invalidates it. */
  phone: string
  hash: string
}

/**
 * `09121234567` → `+989121234567`.
 *
 * Google Ads only matches a hashed phone number in E.164, so hashing the local
 * form produces a value that can never match a conversion. Anything that is
 * not an Iranian mobile returns null: sending nothing beats sending a hash of
 * a malformed number.
 */
export function toE164IranMobile(raw: string | null | undefined): string | null {
  const digits = String(raw ?? '').replace(/\D/g, '').replace(/^0+/, '')
  const local = /^9\d{9}$/.test(digits)
    ? digits
    : digits.startsWith('98') && /^9\d{9}$/.test(digits.slice(2))
      ? digits.slice(2)
      : null
  return local ? `+98${local}` : null
}

/**
 * Lowercase-hex SHA-256, the encoding Google Ads expects for hashed user data.
 * `crypto.subtle` exists only in a secure context, so this returns null over
 * plain http (local dev) instead of throwing.
 */
async function sha256Hex(value: string): Promise<string | null> {
  if (typeof crypto === 'undefined' || !crypto.subtle) return null
  try {
    const digest = await crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(value),
    )
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
  } catch {
    return null
  }
}

function readCachedPhoneHash(userId: string, e164: string): string | null {
  try {
    const raw = localStorage.getItem(PHONE_HASH_KEY)
    if (!raw) return null
    const cached = JSON.parse(raw) as Partial<CachedPhoneHash> | null
    return cached?.userId === userId && cached.phone === e164 && cached.hash
      ? cached.hash
      : null
  } catch {
    return null
  }
}

/** Drops the cached hash. Called on logout — it identifies a person. */
export function clearCachedPhoneHash() {
  try {
    localStorage.removeItem(PHONE_HASH_KEY)
  } catch {
    /* ignore */
  }
}

function publishIdentity(userId: string, phoneHash: string | null) {
  const signature = `${userId}|${phoneHash ?? ''}`
  if (window.__gtmIdentity === signature) return
  window.__gtmIdentity = signature
  pushDataLayerEvent({
    user_id: userId,
    ...(phoneHash ? { user_phone_hashed: phoneHash } : {}),
  })
}

/**
 * Puts the signed-in visitor on the dataLayer so every tag that fires
 * afterwards can attribute its event to them.
 *
 * The hash is published in the same push when it is already cached — the
 * normal case. The first time (right after login) it arrives in a second push
 * once Web Crypto resolves; `user_id` is not held back waiting for it, because
 * the events on this page fire immediately and would otherwise carry no user
 * at all.
 */
export function pushUserIdentity(
  user: { id: string; mobileNumber?: string | null } | null | undefined,
) {
  if (typeof window === 'undefined' || !user?.id) return

  const e164 = toE164IranMobile(user.mobileNumber)
  const cached = e164 ? readCachedPhoneHash(user.id, e164) : null
  publishIdentity(user.id, cached)
  if (!e164 || cached) return

  const userId = user.id
  void sha256Hex(e164).then((hash) => {
    if (!hash) return
    try {
      localStorage.setItem(
        PHONE_HASH_KEY,
        JSON.stringify({ userId, phone: e164, hash } satisfies CachedPhoneHash),
      )
    } catch {
      /* ignore */
    }
    publishIdentity(userId, hash)
  })
}

/**
 * Inline <head> script, rendered immediately *before* the GTM snippet in the
 * root layout: marketing's spec requires `user_id` / `user_phone_hashed` to be
 * the dataLayer's initial state on the consultation-request form, not a push
 * that races the container.
 *
 * It is scoped to `/orders/new` (with or without a locale prefix) because that
 * is the page the spec covers; a client-side navigation there is handled by
 * `pushUserIdentity` instead, which this script agrees with through
 * `window.__gtmIdentity`.
 *
 * Deliberately synchronous and dependency-free — it reads the session that
 * AuthContext keeps in localStorage plus the pre-computed phone hash, and
 * stays silent on any failure rather than blocking the document.
 */
export const gtmIdentityScript = `(function(){try{
if(!/^\\/(?:[a-z]{2}(?:-[a-z]{2})?\\/)?orders\\/new\\/?$/i.test(location.pathname))return;
var u=JSON.parse(localStorage.getItem('user')||'null');
if(!u||!u.id)return;
var d={user_id:String(u.id)};
var c=JSON.parse(localStorage.getItem('${PHONE_HASH_KEY}')||'null');
if(c&&c.hash&&c.userId===String(u.id))d.user_phone_hashed=c.hash;
window.dataLayer=window.dataLayer||[];
window.dataLayer.push(d);
window.__gtmIdentity=d.user_id+'|'+(d.user_phone_hashed||'');
}catch(e){}})();`
